// src/components/AdminDashboard.jsx - Redesigned HIPAA-compliant analytics dashboard
import React, { useState, useEffect, useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie
} from 'recharts';

// ============================================================================
// PDF REPORT GENERATION UTILITIES (RESTORED)
// ============================================================================

// Dynamic imports for PDF generation
const loadPdfLibraries = async () => {
  const [html2canvasModule, jsPDFModule] = await Promise.all([
    import('html2canvas'),
    import('jspdf')
  ]);
  return {
    html2canvas: html2canvasModule.default,
    jsPDF: jsPDFModule.jsPDF
  };
};

// Generate doctor report HTML
const generateDoctorReportHTML = (doctor, reportData) => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Monthly Report - ${doctor.name || doctor.url_slug}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 40px; background: #f5f5f5; }
        .container { max-width: 800px; margin: 0 auto; background: white; padding: 40px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
        .header { text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 2px solid #f0f0f0; }
        .logo { font-size: 24px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px; }
        .subtitle { color: #666; font-size: 14px; }
        .doctor-name { font-size: 28px; font-weight: 600; color: #1a1a1a; margin: 20px 0 8px; }
        .report-date { color: #999; font-size: 14px; }
        .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 30px 0; }
        .stat-card { background: #fafafa; padding: 24px; border-radius: 12px; text-align: center; }
        .stat-value { font-size: 36px; font-weight: 700; color: #1a1a1a; }
        .stat-label { color: #666; font-size: 14px; margin-top: 8px; }
        .section { margin: 30px 0; }
        .section-title { font-size: 18px; font-weight: 600; color: #1a1a1a; margin-bottom: 16px; }
        .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #f0f0f0; color: #999; font-size: 12px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="logo">my-skin.ai</div>
          <div class="subtitle">Monthly Performance Report</div>
          <div class="doctor-name">${doctor.name || doctor.url_slug}</div>
          <div class="report-date">Generated on ${currentDate}</div>
        </div>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">${reportData.totalReports || 0}</div>
            <div class="stat-label">Total Reports</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${reportData.thisMonth || 0}</div>
            <div class="stat-label">This Month</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${reportData.whatsappClicks || 0}</div>
            <div class="stat-label">WhatsApp Clicks</div>
          </div>
        </div>
        <div class="section">
          <div class="section-title">Performance Summary</div>
          <p style="color: #666; line-height: 1.6;">
            This report summarizes the patient engagement metrics for your clinic page on my-skin.ai.
            The data reflects patient interactions including skin analysis reports generated and booking actions taken.
          </p>
        </div>
        <div class="footer">
          <p>This report is confidential and intended for authorized personnel only.</p>
          <p>HIPAA Compliant • my-skin.ai © ${new Date().getFullYear()}</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

// View report in new tab - uses the comprehensive simple-monthly-reports API
const viewDoctorReport = async (doctor) => {
  try {
    const user = localStorage.getItem('admin_user');
    const pass = localStorage.getItem('admin_pass');
    if (!user || !pass) {
      alert('Authentication required');
      return;
    }

    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format

    const response = await fetch('/api/admin/simple-monthly-reports', {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`${user}:${pass}`)}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'generate_html',
        doctorId: doctor.id,
        reportMonth: currentMonth
      })
    });

    if (response.ok) {
      const html = await response.text();
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(html);
        newWindow.document.close();
      }
    } else {
      // Fallback to basic report if API fails
      const reportData = {
        totalReports: doctor.total_direct_patients || 0,
        thisMonth: doctor.this_month_patients || 0,
        whatsappClicks: doctor.whatsapp_clicks || 0
      };
      const html = generateDoctorReportHTML(doctor, reportData);
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(html);
        newWindow.document.close();
      }
    }
  } catch (error) {
    alert('Failed to generate report. Please try again.');
  }
};

// Download report as PDF - uses the comprehensive simple-monthly-reports API
const downloadDoctorReport = async (doctor) => {
  try {
    const user = localStorage.getItem('admin_user');
    const pass = localStorage.getItem('admin_pass');
    if (!user || !pass) {
      alert('Authentication required');
      return;
    }

    const { html2canvas, jsPDF } = await loadPdfLibraries();
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format

    // Try to get HTML from the API first
    let htmlContent;
    try {
      const response = await fetch('/api/admin/simple-monthly-reports', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${user}:${pass}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'generate_pdf',
          doctorId: doctor.id,
          reportMonth: currentMonth
        })
      });

      if (response.ok) {
        const result = await response.json();
        htmlContent = result.htmlContent;
      }
    } catch (apiError) {
    }

    // Fallback to basic report if API fails
    if (!htmlContent) {
      const reportData = {
        totalReports: doctor.total_direct_patients || 0,
        thisMonth: doctor.this_month_patients || 0,
        whatsappClicks: doctor.whatsapp_clicks || 0
      };
      htmlContent = generateDoctorReportHTML(doctor, reportData);
    }

    // Create temporary container
    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '210mm';
    document.body.appendChild(container);

    // Wait for styles to apply
    await new Promise(resolve => setTimeout(resolve, 200));

    // Find the main content container
    const contentEl = container.querySelector('.pdf-container') ||
                      container.querySelector('.container') ||
                      container.firstElementChild;

    // Capture as canvas
    const canvas = await html2canvas(contentEl, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: 794, // A4 width at 96 DPI
      windowWidth: 794
    });

    // Convert to PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    // Handle multi-page if needed
    const pageHeight = pdf.internal.pageSize.getHeight();
    let heightLeft = pdfHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`report-${doctor.url_slug || doctor.id}-${currentMonth}.pdf`);

    // Cleanup
    document.body.removeChild(container);
  } catch (error) {
    alert('Failed to generate PDF. Please try again.');
  }
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auth, setAuth] = useState({ username: '', password: '' });
  const [authenticated, setAuthenticated] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [apiUsageSnapshot, setApiUsageSnapshot] = useState([]);
  const [apiUsageSnapshotAt, setApiUsageSnapshotAt] = useState(null);

  const credentialsRef = useRef(null);

  // State for pending key requests count
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  // Navigation items - consolidated from 8 tabs to 4 logical sections + API Keys + Key Requests
  const navItems = [
    { id: 'overview', label: 'Overview', icon: BarChartIcon },
    { id: 'referrals', label: 'Referrals', icon: UsersIcon },
    { id: 'doctors', label: 'Doctors', icon: StethoscopeIcon },
    { id: 'activity', label: 'Activity', icon: ActivityIcon },
    { id: 'api-keys', label: 'API Keys', icon: KeyIcon },
    { id: 'key-requests', label: 'Key Requests', icon: InboxIcon, badge: pendingRequestsCount },
    { id: 'invoices', label: 'Invoices', icon: InvoiceIcon },
  ];

  // Auto-login on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('admin_user');
    const savedPass = localStorage.getItem('admin_pass');

    if (savedUser && savedPass) {
      const autoLogin = async () => {
        try {
          setLoading(true);
          const credentials = btoa(`${savedUser}:${savedPass}`);
          const response = await fetch('/api/admin/metrics', {
            method: 'GET',
            headers: {
              'Authorization': `Basic ${credentials}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const metrics = await response.json();
            setData(metrics);
            setAuthenticated(true);
            setAuth({ username: savedUser, password: savedPass });
            setError(null);
            credentialsRef.current = credentials;
            await fetchApiUsageSnapshot(savedUser, savedPass);
          } else {
            localStorage.removeItem('admin_user');
            localStorage.removeItem('admin_pass');
          }
        } catch (err) {
          localStorage.removeItem('admin_user');
          localStorage.removeItem('admin_pass');
        } finally {
          setLoading(false);
        }
      };
      autoLogin();
    }
  }, []);

  const fetchApiUsageSnapshot = async (username, password) => {
    try {
      const response = await fetch('/api/admin/api-keys', {
        headers: {
          'Authorization': `Basic ${btoa(`${username}:${password}`)}`
        }
      });
      if (!response.ok) return;
      const result = await response.json();
      setApiUsageSnapshot(result.keys || []);
      setApiUsageSnapshotAt(new Date().toISOString());
    } catch {
      // Silent fail - overview metrics still load
    }
  };

  const login = async () => {
    try {
      setLoading(true);
      setError(null);

      const credentials = btoa(`${auth.username}:${auth.password}`);
      const response = await Promise.race([
        fetch('/api/admin/metrics', {
          method: 'GET',
          headers: {
            'Authorization': `Basic ${credentials}`,
            'Content-Type': 'application/json'
          }
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 30000)
        )
      ]);

      if (response.ok) {
        const metrics = await response.json();
        setData(metrics);
        setAuthenticated(true);
        credentialsRef.current = credentials;
        await fetchApiUsageSnapshot(auth.username, auth.password);
        try {
          localStorage.setItem('admin_user', auth.username);
          localStorage.setItem('admin_pass', auth.password);
        } catch {}
        setError(null);
      } else if (response.status === 401) {
        setError('Invalid credentials');
      } else {
        setError(`Server error: ${response.status}`);
      }
    } catch (err) {
      if (err.message.includes('timeout')) {
        setError('Connection timed out. Please try again.');
      } else {
        setError('Connection failed. Check your internet.');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    if (!authenticated || !credentialsRef.current) return;
    try {
      setRefreshing(true);
      const response = await fetch('/api/admin/metrics', {
        headers: { 'Authorization': `Basic ${credentialsRef.current}` }
      });
      if (response.ok) {
        const metrics = await response.json();
        setData(metrics);
        await fetchApiUsageSnapshot(auth.username, auth.password);
      }
    } catch (err) {
      // Silent fail
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    login();
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_pass');
    setAuthenticated(false);
    setAuth({ username: '', password: '' });
    setError(null);
    setData(null);
    setApiUsageSnapshot([]);
    setApiUsageSnapshotAt(null);
    credentialsRef.current = null;
  };

  // Reset function to clear all cached data (from original)
  const handleReset = () => {
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_pass');
    localStorage.setItem('force_logout', 'true');
    setAuthenticated(false);
    setAuth({ username: '', password: '' });
    setError(null);
    setData(null);
    setApiUsageSnapshot([]);
    setApiUsageSnapshotAt(null);
    credentialsRef.current = null;
  };

  // ============================================================================
  // LOGIN SCREEN
  // ============================================================================

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1a1a1a] rounded-xl mb-4">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-[#1a1a1a] tracking-tight">Admin Dashboard</h1>
            <p className="text-sm text-[#666] mt-1">Sign in to continue</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm border border-[#e5e5e5]">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#666] uppercase tracking-wide mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={auth.username}
                  onChange={(e) => setAuth({...auth, username: e.target.value})}
                  className="w-full px-4 py-3 bg-[#fafafa] border border-[#e5e5e5] rounded-xl text-[#1a1a1a] placeholder-[#999] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent transition-all"
                  placeholder="Enter username"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#666] uppercase tracking-wide mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={auth.password}
                  onChange={(e) => setAuth({...auth, password: e.target.value})}
                  className="w-full px-4 py-3 bg-[#fafafa] border border-[#e5e5e5] rounded-xl text-[#1a1a1a] placeholder-[#999] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent transition-all"
                  placeholder="Enter password"
                  required
                />
              </div>

              {error && (
                <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-[#1a1a1a] text-white rounded-xl font-medium hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <span className="inline-flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : 'Sign in'}
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="w-full py-2.5 px-4 bg-[#f5f5f5] text-[#666] rounded-xl font-medium hover:bg-[#e5e5e5] transition-all text-sm"
              >
                Clear Cache & Reset
              </button>
            </div>
          </form>

          <p className="text-center text-xs text-[#999] mt-6">
            HIPAA Compliant Dashboard
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#666]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN DASHBOARD LAYOUT
  // ============================================================================

  return (
    <div className="min-h-screen bg-[#fafafa] flex">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-56'} bg-[#1a1a1a] flex flex-col transition-all duration-300 ease-in-out`}>
        {/* Logo */}
        <div className="p-4 border-b border-[#333]">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            {!sidebarCollapsed && (
              <span className="text-white font-semibold text-sm">my-skin.ai</span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                activeSection === item.id
                  ? 'bg-white/10 text-white'
                  : 'text-[#888] hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <div className="flex items-center justify-between flex-1">
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
              {sidebarCollapsed && item.badge > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Collapse Toggle & Logout */}
        <div className="p-3 border-t border-[#333] space-y-1">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-[#888] hover:text-white hover:bg-white/5 transition-all"
          >
            <ChevronIcon className={`w-5 h-5 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
            {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-[#888] hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogoutIcon className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="sticky top-0 z-10 bg-[#fafafa]/80 backdrop-blur-xl border-b border-[#e5e5e5]">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-[#1a1a1a] capitalize">
                {activeSection === 'overview' ? 'Dashboard Overview' : activeSection}
              </h1>
              <p className="text-sm text-[#666]">
                {data?.timestamp ? `Last updated ${new Date(data.timestamp).toLocaleTimeString()}` : ''}
              </p>
            </div>
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-[#e5e5e5] rounded-lg text-sm font-medium text-[#666] hover:text-[#1a1a1a] hover:border-[#ccc] disabled:opacity-50 transition-all"
            >
              <RefreshIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {activeSection === 'overview' && (
            <OverviewSection
              data={data}
              apiUsageSnapshot={apiUsageSnapshot}
              apiUsageSnapshotAt={apiUsageSnapshotAt}
            />
          )}
          {activeSection === 'referrals' && <ReferralsSection data={data} credentials={auth} />}
          {activeSection === 'doctors' && <DoctorsSection data={data} />}
          {activeSection === 'activity' && <ActivitySection data={data} />}
          {activeSection === 'api-keys' && <APIKeysTab credentials={auth} />}
          {activeSection === 'key-requests' && <KeyRequestsTab credentials={auth} onCountChange={setPendingRequestsCount} />}
          {activeSection === 'invoices' && <InvoicesSection credentials={auth} data={data} />}
        </div>
      </main>
    </div>
  );
};

// ============================================================================
// OVERVIEW SECTION
// ============================================================================

const OverviewSection = ({ data, apiUsageSnapshot = [], apiUsageSnapshotAt = null }) => {
  const [doctorChartPeriod, setDoctorChartPeriod] = useState(30);
  const [mainSiteChartPeriod, setMainSiteChartPeriod] = useState(30);

  // Auto-scroll refs for charts (restored from original)
  const doctorChartScrollRef = useRef(null);
  const mainSiteChartScrollRef = useRef(null);

  // Auto-scroll to latest data when period changes
  useEffect(() => {
    if (doctorChartScrollRef.current) {
      doctorChartScrollRef.current.scrollLeft = doctorChartScrollRef.current.scrollWidth;
    }
  }, [doctorChartPeriod]);

  useEffect(() => {
    if (mainSiteChartScrollRef.current) {
      mainSiteChartScrollRef.current.scrollLeft = mainSiteChartScrollRef.current.scrollWidth;
    }
  }, [mainSiteChartPeriod]);

  if (!data) return <LoadingState />;

  // Calculate metrics
  const totalReports = data.lifetimeTotals?.doctorReports || data.totals?.total_reports || 0;
  const whatsappBookings = data.totals?.total_whatsapp_bookings || 0;
  const doctorMatches = data.totals?.total_doctor_matches || 0;
  const directionsClicks = data.totals?.total_directions_clicks || 0;

  const matchRate = data.conversion?.match_rate ||
    (totalReports > 0 ? Math.round((doctorMatches / totalReports) * 100) : 0);
  const bookingRate = data.conversion?.booking_rate ||
    (doctorMatches > 0 ? Math.round((whatsappBookings / doctorMatches) * 100) : 0);

  // Period-over-period changes (from API)
  const periodChanges = data.periodChanges || {};
  const formatChange = (change) => {
    if (change === undefined || change === null) return null;
    if (change === 0) return '0%';
    return change > 0 ? `+${change}%` : `${change}%`;
  };
  const getTrend = (change) => {
    if (change === undefined || change === null || change === 0) return 'stable';
    return change > 0 ? 'up' : 'down';
  };

  // Doctor Page Chart data
  const doctorChartData = (data.doctorPageSeries || data.series || [])
    .slice(-doctorChartPeriod)
    .map(d => ({
      day: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      reports: d.reports || 0
    }));

  // Main Site Chart data
  const mainSiteChartData = (data.mainSiteSeries || [])
    .slice(-mainSiteChartPeriod)
    .map(d => ({
      day: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      reports: d.reports || 0
    }));

  // Event distribution for PieChart
  const eventDistribution = [
    { name: 'Reports', value: totalReports, color: '#1a1a1a' },
    { name: 'WhatsApp', value: whatsappBookings, color: '#22c55e' },
    { name: 'Matches', value: doctorMatches, color: '#8b5cf6' },
    { name: 'Directions', value: directionsClicks, color: '#f97316' }
  ].filter(e => e.value > 0);

  const now = new Date();
  const isFirstDayOfMonth = now.getDate() === 1;
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const keyUsageRows = (apiUsageSnapshot || [])
    .map((key) => {
      const resetMonth = Number(key.usage_reset_month || 0);
      const resetYear = Number(key.usage_reset_year || 0);
      const inCurrentMonth = resetMonth === currentMonth && resetYear === currentYear;
      const usageThisMonth = inCurrentMonth ? Number(key.current_month_usage || 0) : 0;
      return {
        id: key.id,
        keyName: key.key_name || 'Unnamed key',
        keyPrefix: key.key_prefix || 'msk_...',
        usageThisMonth,
        monthlyLimit: key.monthly_limit
      };
    })
    .sort((a, b) => b.usageThisMonth - a.usageThisMonth);

  return (
    <div className="space-y-6">
      {/* Day-1 monthly API usage alert */}
      {isFirstDayOfMonth && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-amber-900">Monthly API Usage Alert</h3>
              <p className="text-sm text-amber-700">
                Snapshot for {now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
              </p>
            </div>
            {apiUsageSnapshotAt && (
              <span className="text-xs text-amber-700">
                Refreshed {new Date(apiUsageSnapshotAt).toLocaleTimeString('en-US')}
              </span>
            )}
          </div>
          {keyUsageRows.length === 0 ? (
            <p className="text-sm text-amber-800">No API keys found.</p>
          ) : (
            <div className="space-y-2">
              {keyUsageRows.slice(0, 8).map((row) => (
                <div key={row.id} className="flex items-center justify-between text-sm bg-white/60 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-medium text-amber-900 truncate">{row.keyName}</p>
                    <p className="text-xs text-amber-700">{row.keyPrefix}</p>
                  </div>
                  <p className="font-semibold text-amber-900">
                    {row.usageThisMonth.toLocaleString()}
                    {row.monthlyLimit ? ` / ${Number(row.monthlyLimit).toLocaleString()}` : ' / unlimited'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Reports"
          value={totalReports.toLocaleString()}
          change={formatChange(periodChanges.reports?.change)}
          trend={getTrend(periodChanges.reports?.change)}
          icon={<DocumentIcon className="w-5 h-5" />}
        />
        <MetricCard
          label="WhatsApp Bookings"
          value={whatsappBookings.toLocaleString()}
          change={formatChange(periodChanges.whatsapp?.change)}
          trend={getTrend(periodChanges.whatsapp?.change)}
          icon={<MessageIcon className="w-5 h-5" />}
          accent="green"
        />
        <MetricCard
          label="Doctor Matches"
          value={doctorMatches.toLocaleString()}
          change={formatChange(periodChanges.matches?.change)}
          trend={getTrend(periodChanges.matches?.change)}
          icon={<UsersIcon className="w-5 h-5" />}
          accent="purple"
        />
        <MetricCard
          label="Directions"
          value={directionsClicks.toLocaleString()}
          change={formatChange(periodChanges.directions?.change)}
          trend={getTrend(periodChanges.directions?.change)}
          icon={<MapIcon className="w-5 h-5" />}
          accent="orange"
        />
      </div>

      {/* Dual Charts - Doctor Pages & Main Site with separate zoom controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Doctor Page Reports Chart */}
        <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-[#1a1a1a]">Doctor Page Reports</h3>
              <p className="text-sm text-[#666]">Direct patient submissions</p>
            </div>
            <div className="flex items-center space-x-1 bg-[#f5f5f5] rounded-lg p-1">
              {[7, 14, 30, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setDoctorChartPeriod(days)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                    doctorChartPeriod === days
                      ? 'bg-white text-[#1a1a1a] shadow-sm'
                      : 'text-[#666] hover:text-[#1a1a1a]'
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <div ref={doctorChartScrollRef} className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={doctorChartData.length > 0 ? doctorChartData : [{ day: 'No data', reports: 0 }]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="day" stroke="#999" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#999" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: '8px', color: 'white', fontSize: '12px' }}
                />
                <Line type="monotone" dataKey="reports" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#8b5cf6' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Main Site Visits Chart */}
        <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-[#1a1a1a]">Main Site Activity</h3>
              <p className="text-sm text-[#666]">General visitor engagement</p>
            </div>
            <div className="flex items-center space-x-1 bg-[#f5f5f5] rounded-lg p-1">
              {[7, 14, 30, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setMainSiteChartPeriod(days)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                    mainSiteChartPeriod === days
                      ? 'bg-white text-[#1a1a1a] shadow-sm'
                      : 'text-[#666] hover:text-[#1a1a1a]'
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <div ref={mainSiteChartScrollRef} className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mainSiteChartData.length > 0 ? mainSiteChartData : [{ day: 'No data', reports: 0 }]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="day" stroke="#999" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#999" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: '8px', color: 'white', fontSize: '12px' }}
                />
                <Line type="monotone" dataKey="reports" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#3b82f6' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Conversion Funnel BarChart */}
      <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6">
        <h3 className="font-semibold text-[#1a1a1a] mb-4">Conversion Funnel</h3>
        <p className="text-sm text-[#666] mb-6">End-to-end patient journey</p>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart
              data={[
                { stage: 'Reports', value: totalReports, fill: '#1a1a1a' },
                { stage: 'Matches', value: doctorMatches, fill: '#8b5cf6' },
                { stage: 'WhatsApp', value: whatsappBookings, fill: '#22c55e' },
                { stage: 'Directions', value: directionsClicks, fill: '#f97316' },
              ]}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} />
              <XAxis type="number" stroke="#999" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis dataKey="stage" type="category" stroke="#999" fontSize={12} tickLine={false} axisLine={false} width={70} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: '8px', color: 'white', fontSize: '12px' }}
                formatter={(value) => [value.toLocaleString(), 'Count']}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {[
                  { stage: 'Reports', value: totalReports, fill: '#1a1a1a' },
                  { stage: 'Matches', value: doctorMatches, fill: '#8b5cf6' },
                  { stage: 'WhatsApp', value: whatsappBookings, fill: '#22c55e' },
                  { stage: 'Directions', value: directionsClicks, fill: '#f97316' },
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Funnel Metrics */}
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#f0f0f0]">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#1a1a1a]">{matchRate}%</p>
            <p className="text-xs text-[#666]">Match Rate</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#22c55e]">{bookingRate}%</p>
            <p className="text-xs text-[#666]">Booking Rate</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#8b5cf6]">{data.conversion?.overall_conversion || Math.round((whatsappBookings / Math.max(totalReports, 1)) * 100)}%</p>
            <p className="text-xs text-[#666]">Overall</p>
          </div>
        </div>
      </div>

      {/* Event Distribution PieChart & Conversion Rates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Event Distribution PieChart */}
        <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6">
          <h3 className="font-semibold text-[#1a1a1a] mb-4">Event Distribution</h3>
          <div className="h-48">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={eventDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {eventDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: '8px', color: 'white', fontSize: '12px' }}
                  formatter={(value, name) => [value.toLocaleString(), name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {eventDistribution.map((item, idx) => (
              <div key={idx} className="flex items-center space-x-2 text-xs">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-[#666]">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Conversion Rates */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#e5e5e5] p-6">
          <h3 className="font-semibold text-[#1a1a1a] mb-6">Conversion Funnel</h3>
          <div className="space-y-5">
            <ConversionMetric
              label="Match Rate"
              description="Reports → Doctor Selection"
              value={matchRate}
              color="#1a1a1a"
            />
            <ConversionMetric
              label="Booking Rate"
              description="Selection → WhatsApp"
              value={bookingRate}
              color="#22c55e"
            />
            <ConversionMetric
              label="Overall Conversion"
              description="End-to-End (Report → Booking)"
              value={data.conversion?.overall_conversion || Math.round((whatsappBookings / Math.max(totalReports, 1)) * 100)}
              color="#8b5cf6"
            />
            <ConversionMetric
              label="Directions Rate"
              description="Selection → Directions"
              value={doctorMatches > 0 ? Math.round((directionsClicks / doctorMatches) * 100) : 0}
              color="#f97316"
            />
          </div>
        </div>
      </div>

      {/* Source Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SourceCard
          title="Doctor Page Reports"
          subtitle="Verified patient data"
          value={data.sourceBreakdown?.doctorPages?.lifetime || 0}
          stats={[
            { label: '90 days', value: data.sourceBreakdown?.doctorPages?.last90Days || 0 },
            { label: '30 days', value: data.sourceBreakdown?.doctorPages?.last30Days || 0 },
            { label: '7 days', value: data.sourceBreakdown?.doctorPages?.last7Days || 0 },
          ]}
          accent="purple"
        />
        <SourceCard
          title="Main Site Reports"
          subtitle="From Jan 2026"
          value={data.sourceBreakdown?.mainSite?.sinceJan2026 || 0}
          stats={[
            { label: 'WhatsApp', value: data.sourceBreakdown?.engagement?.whatsappClicks || 0 },
            { label: 'Directions', value: data.sourceBreakdown?.engagement?.directionsClicks || 0 },
            { label: 'Active', value: data.sourceBreakdown?.doctorPages?.active || 0 },
          ]}
          accent="blue"
        />
      </div>
    </div>
  );
};

// ============================================================================
// REFERRALS SECTION (Combines Patients + Main Site Referrals)
// ============================================================================

const ReferralsSection = ({ data, credentials }) => {
  const [activeTab, setActiveTab] = useState('recent');
  const [recentPatients, setRecentPatients] = useState(null);
  const [mainSiteReferrals, setMainSiteReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ interactionType: '' });

  // Load recent patients
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadRecentPatients = async () => {
      try {
        setLoading(true);
        setError(null);
        const creds = localStorage.getItem('admin_user') && localStorage.getItem('admin_pass')
          ? btoa(`${localStorage.getItem('admin_user')}:${localStorage.getItem('admin_pass')}`)
          : null;

        if (!creds) {
          setError('Authentication required');
          return;
        }

        const response = await fetch('/api/admin/recent-patients', {
          headers: { 'Authorization': `Basic ${creds}` }
        });

        if (response.ok) {
          const result = await response.json();
          setRecentPatients(result);
        } else {
          setError(`Failed to load patients: ${response.status}`);
        }
      } catch (err) {
        setError('Failed to load recent patients');
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'recent') loadRecentPatients();
  }, [activeTab]);

  // Load main site referrals
  useEffect(() => {
    const loadMainSiteReferrals = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (filters.interactionType) params.append('interactionType', filters.interactionType);
        params.append('limit', '50');

        const response = await fetch(`/api/admin/main-site-referrals?${params}`, {
          headers: {
            'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          setMainSiteReferrals(result.referrals || []);
        } else {
          setError(`Failed to load referrals: ${response.status}`);
        }
      } catch (err) {
        setError('Failed to load main site referrals');
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'mainsite') loadMainSiteReferrals();
  }, [activeTab, filters, credentials]);

  const filteredPatients = recentPatients?.recent_patients?.filter(patient => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      patient.patient_name?.toLowerCase().includes(searchLower) ||
      patient.patient_phone?.includes(searchTerm) ||
      patient.clinic_name?.toLowerCase().includes(searchLower)
    );
  }) || [];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center space-x-1 bg-white border border-[#e5e5e5] rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('recent')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'recent'
              ? 'bg-[#1a1a1a] text-white'
              : 'text-[#666] hover:text-[#1a1a1a]'
          }`}
        >
          Recent Patients
        </button>
        <button
          onClick={() => setActiveTab('mainsite')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'mainsite'
              ? 'bg-[#1a1a1a] text-white'
              : 'text-[#666] hover:text-[#1a1a1a]'
          }`}
        >
          Main Site Referrals
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
          <input
            type="text"
            placeholder="Search patients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent"
          />
        </div>
        {activeTab === 'mainsite' && (
          <select
            value={filters.interactionType}
            onChange={(e) => setFilters({ ...filters, interactionType: e.target.value })}
            className="px-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]"
          >
            <option value="">All Types</option>
            <option value="doctor_selected">Doctor Selected</option>
            <option value="directions_clicked">Directions</option>
            <option value="whatsapp_clicked">WhatsApp</option>
          </select>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Content */}
      <div className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-6 h-6 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-[#666]">Loading...</p>
          </div>
        ) : activeTab === 'recent' ? (
          <RecentPatientsTable patients={filteredPatients} />
        ) : (
          <MainSiteReferralsTable referrals={mainSiteReferrals} />
        )}
      </div>
    </div>
  );
};

const RecentPatientsTable = ({ patients }) => {
  if (!patients || patients.length === 0) {
    return (
      <div className="p-12 text-center">
        <UsersIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
        <p className="text-[#666]">No patients found</p>
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
        <tr>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Date</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Patient</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Phone</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Clinic</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#f0f0f0]">
        {patients.map((patient, idx) => (
          <tr key={patient.id || idx} className="hover:bg-[#fafafa] transition-colors">
            <td className="px-6 py-4 text-sm text-[#666]">
              {new Date(patient.created_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </td>
            <td className="px-6 py-4">
              <span className="font-medium text-[#1a1a1a]">{patient.patient_name || 'Anonymous'}</span>
            </td>
            <td className="px-6 py-4 text-sm text-[#666] font-mono">{patient.patient_phone || '—'}</td>
            <td className="px-6 py-4 text-sm text-[#666]">{patient.clinic_name || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const MainSiteReferralsTable = ({ referrals }) => {
  if (!referrals || referrals.length === 0) {
    return (
      <div className="p-12 text-center">
        <MapIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
        <p className="text-[#666]">No referrals found</p>
      </div>
    );
  }

  const getInteractionBadge = (type) => {
    const styles = {
      doctor_selected: 'bg-blue-50 text-blue-600',
      directions_clicked: 'bg-purple-50 text-purple-600',
      whatsapp_clicked: 'bg-green-50 text-green-600'
    };
    const labels = {
      doctor_selected: 'Selected',
      directions_clicked: 'Directions',
      whatsapp_clicked: 'WhatsApp'
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${styles[type] || 'bg-gray-50 text-gray-600'}`}>
        {labels[type] || type}
      </span>
    );
  };

  return (
    <table className="w-full">
      <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
        <tr>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Date</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Patient</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Doctor</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Type</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Location</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#f0f0f0]">
        {referrals.map((ref) => (
          <tr key={ref.id} className="hover:bg-[#fafafa] transition-colors">
            <td className="px-6 py-4 text-sm text-[#666]">
              {new Date(ref.created_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </td>
            <td className="px-6 py-4">
              <span className="font-medium text-[#1a1a1a]">{ref.patient_name || 'Anonymous'}</span>
              {ref.patient_phone && (
                <p className="text-xs text-[#999] font-mono mt-0.5">{ref.patient_phone}</p>
              )}
            </td>
            <td className="px-6 py-4 text-sm text-[#1a1a1a]">{ref.doctor_name || ref.doctor_id}</td>
            <td className="px-6 py-4">{getInteractionBadge(ref.interaction_type)}</td>
            <td className="px-6 py-4 text-sm text-[#666]">
              {[ref.patient_city, ref.patient_state].filter(Boolean).join(', ') || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ============================================================================
// DOCTORS SECTION (Combines Manage + Whitelabeled + Top Doctors)
// ============================================================================

const DoctorsSection = ({ data }) => {
  const [activeTab, setActiveTab] = useState('manage');
  const [doctors, setDoctors] = useState([]);
  const [whitelabeledDocs, setWhitelabeledDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [passwordResetDoctor, setPasswordResetDoctor] = useState(null);
  const [passwordResetForm, setPasswordResetForm] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState('');
  const [formData, setFormData] = useState({
    name: '', phone_e164: '', whatsapp_e164: '', address: '',
    lat: '', lng: '', partner_priority: 50, maps_url: '', photo_url: ''
  });

  const getCredentials = () => {
    const user = localStorage.getItem('admin_user');
    const pass = localStorage.getItem('admin_pass');
    return user && pass ? btoa(`${user}:${pass}`) : null;
  };

  // Load doctors
  useEffect(() => {
    const loadDoctors = async () => {
      const creds = getCredentials();
      if (!creds) {
        setError('Authentication required');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
        const res = await fetch(`/api/admin/doctors${params}`, {
          headers: { 'Authorization': `Basic ${creds}` }
        });
        if (res.ok) {
          const result = await res.json();
          setDoctors(result.doctors || []);
        } else {
          setError(`Failed to load doctors: ${res.status}`);
        }
      } catch (err) {
        setError('Failed to load doctors');
      }
      setLoading(false);
    };
    if (activeTab === 'manage') loadDoctors();
  }, [activeTab, searchTerm]);

  // Load whitelabeled docs
  useEffect(() => {
    const loadWhitelabeled = async () => {
      const creds = getCredentials();
      if (!creds) {
        setError('Authentication required');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/admin/whitelabeled-docs', {
          headers: { 'Authorization': `Basic ${creds}` }
        });
        if (res.ok) {
          const result = await res.json();
          setWhitelabeledDocs(result.doctors || []);
        } else {
          setError(`Failed to load whitelabeled doctors: ${res.status}`);
        }
      } catch (err) {
        setError('Failed to load whitelabeled doctors');
      }
      setLoading(false);
    };
    if (activeTab === 'whitelabel') loadWhitelabeled();
  }, [activeTab]);

  // Update doctor monthly limit
  const updateDoctorLimit = async (doctorId, newLimit) => {
    const creds = getCredentials();
    if (!creds) return;

    try {
      const res = await fetch('/api/admin/doctor-limits', {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${creds}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ doctorId, monthlyLimit: newLimit })
      });

      if (res.ok) {
        // Update local state
        setWhitelabeledDocs(prev =>
          prev.map(doc =>
            doc.id === doctorId ? { ...doc, monthly_limit: newLimit } : doc
          )
        );
      }
    } catch (err) {
    }
  };

  const openPasswordReset = (doctor) => {
    setPasswordResetDoctor(doctor);
    setPasswordResetForm({ newPassword: '', confirmPassword: '' });
    setPasswordResetError('');
  };

  const closePasswordReset = () => {
    setPasswordResetDoctor(null);
    setPasswordResetForm({ newPassword: '', confirmPassword: '' });
    setPasswordResetError('');
  };

  const submitPasswordReset = async (e) => {
    e.preventDefault();
    const creds = getCredentials();
    if (!creds) {
      setPasswordResetError('Authentication required');
      return;
    }

    if (passwordResetForm.newPassword.length < 8) {
      setPasswordResetError('Password must be at least 8 characters long');
      return;
    }

    if (passwordResetForm.newPassword !== passwordResetForm.confirmPassword) {
      setPasswordResetError('New password and confirmation do not match');
      return;
    }

    try {
      setPasswordResetLoading(true);
      setPasswordResetError('');
      const res = await fetch('/api/admin/doctor-password', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${creds}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          doctorId: passwordResetDoctor.id,
          newPassword: passwordResetForm.newPassword,
          confirmPassword: passwordResetForm.confirmPassword
        })
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result.message || result.error || `Failed to reset password: ${res.status}`);
      }

      closePasswordReset();
    } catch (err) {
      setPasswordResetError(err.message || 'Failed to reset password');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const creds = getCredentials();
    if (!creds) {
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const url = editingDoctor ? `/api/admin/doctors?doctorId=${editingDoctor.id}` : '/api/admin/doctors';
      const res = await fetch(url, {
        method: editingDoctor ? 'PUT' : 'POST',
        headers: { 'Authorization': `Basic ${creds}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        resetForm();
        // Reload doctors
        const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
        const reloadRes = await fetch(`/api/admin/doctors${params}`, {
          headers: { 'Authorization': `Basic ${creds}` }
        });
        if (reloadRes.ok) {
          const result = await reloadRes.json();
          setDoctors(result.doctors || []);
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setError(errorData.error || `Failed to ${editingDoctor ? 'update' : 'add'} doctor: ${res.status}`);
      }
    } catch (err) {
      setError(`Failed to ${editingDoctor ? 'update' : 'add'} doctor`);
    }
    setLoading(false);
  };

  const handleEdit = (doctor) => {
    setEditingDoctor(doctor);
    setFormData({
      name: doctor.name || '',
      phone_e164: doctor.phone || '',
      whatsapp_e164: doctor.whatsapp || '',
      address: doctor.address || '',
      lat: doctor.lat?.toString() || '',
      lng: doctor.lng?.toString() || '',
      partner_priority: doctor.partner_priority || 50,
      maps_url: doctor.maps_url || '',
      photo_url: doctor.photo_url || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (doctor) => {
    if (!confirm(`Delete Dr. ${doctor.name}?`)) return;
    const creds = getCredentials();
    if (!creds) {
      setError('Authentication required');
      return;
    }

    try {
      setError(null);
      const res = await fetch(`/api/admin/doctors?doctorId=${doctor.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${creds}` }
      });
      if (res.ok) {
        setDoctors(doctors.filter(d => d.id !== doctor.id));
      } else {
        const errorData = await res.json().catch(() => ({}));
        setError(errorData.error || `Failed to delete doctor: ${res.status}`);
      }
    } catch (err) {
      setError('Failed to delete doctor');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', phone_e164: '', whatsapp_e164: '', address: '',
      lat: '', lng: '', partner_priority: 50, maps_url: '', photo_url: ''
    });
    setEditingDoctor(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1 bg-white border border-[#e5e5e5] rounded-xl p-1">
          {[
            { id: 'manage', label: 'Manage' },
            { id: 'whitelabel', label: 'Whitelabeled' },
            { id: 'top', label: 'Top Performers' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-[#1a1a1a] text-white'
                  : 'text-[#666] hover:text-[#1a1a1a]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'manage' && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-[#1a1a1a] text-white rounded-xl text-sm font-medium hover:bg-[#333] transition-all"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add Doctor</span>
          </button>
        )}
      </div>

      {/* Search (for manage tab) */}
      {activeTab === 'manage' && (
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
          <input
            type="text"
            placeholder="Search doctors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md pl-10 pr-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]"
          />
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#e5e5e5]">
              <h3 className="text-lg font-semibold text-[#1a1a1a]">
                {editingDoctor ? 'Edit Doctor' : 'Add New Doctor'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Name" name="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                <FormField label="Phone (E.164)" name="phone_e164" value={formData.phone_e164} onChange={(e) => setFormData({...formData, phone_e164: e.target.value})} placeholder="+919876543210" required />
                <FormField label="WhatsApp Link" name="whatsapp_e164" value={formData.whatsapp_e164} onChange={(e) => setFormData({...formData, whatsapp_e164: e.target.value})} placeholder="https://wa.me/..." />
                <FormField label="Priority" name="partner_priority" type="number" value={formData.partner_priority} onChange={(e) => setFormData({...formData, partner_priority: parseInt(e.target.value) || 50})} />
                <div className="col-span-2">
                  <FormField label="Address" name="address" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} required />
                </div>
                <FormField label="Latitude" name="lat" type="number" step="any" value={formData.lat} onChange={(e) => setFormData({...formData, lat: e.target.value})} required />
                <FormField label="Longitude" name="lng" type="number" step="any" value={formData.lng} onChange={(e) => setFormData({...formData, lng: e.target.value})} required />
                <FormField label="Maps URL" name="maps_url" value={formData.maps_url} onChange={(e) => setFormData({...formData, maps_url: e.target.value})} />
                <FormField label="Photo URL" name="photo_url" value={formData.photo_url} onChange={(e) => setFormData({...formData, photo_url: e.target.value})} />
              </div>
              <div className="flex items-center space-x-3 pt-4">
                <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-[#1a1a1a] text-white rounded-xl font-medium hover:bg-[#333] disabled:opacity-50 transition-all">
                  {loading ? 'Saving...' : (editingDoctor ? 'Update' : 'Add Doctor')}
                </button>
                <button type="button" onClick={resetForm} className="flex-1 py-2.5 bg-[#f5f5f5] text-[#666] rounded-xl font-medium hover:bg-[#e5e5e5] transition-all">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {passwordResetDoctor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#e5e5e5]">
              <h3 className="text-lg font-semibold text-[#1a1a1a]">Reset Doctor Password</h3>
              <p className="text-sm text-[#666] mt-1">{passwordResetDoctor.url_slug}</p>
            </div>
            <form onSubmit={submitPasswordReset} className="p-6 space-y-4">
              {passwordResetError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-600">{passwordResetError}</p>
                </div>
              )}
              <FormField
                label="New Password"
                name="newPassword"
                type="password"
                value={passwordResetForm.newPassword}
                onChange={(e) => {
                  setPasswordResetForm({ ...passwordResetForm, newPassword: e.target.value });
                  setPasswordResetError('');
                }}
                required
              />
              <FormField
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={passwordResetForm.confirmPassword}
                onChange={(e) => {
                  setPasswordResetForm({ ...passwordResetForm, confirmPassword: e.target.value });
                  setPasswordResetError('');
                }}
                required
              />
              <div className="flex items-center space-x-3 pt-2">
                <button type="submit" disabled={passwordResetLoading} className="flex-1 py-2.5 bg-[#1a1a1a] text-white rounded-xl font-medium hover:bg-[#333] disabled:opacity-50 transition-all">
                  {passwordResetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button type="button" onClick={closePasswordReset} disabled={passwordResetLoading} className="flex-1 py-2.5 bg-[#f5f5f5] text-[#666] rounded-xl font-medium hover:bg-[#e5e5e5] disabled:opacity-50 transition-all">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-6 h-6 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          </div>
        ) : activeTab === 'manage' ? (
          <DoctorsManageTable doctors={doctors} onEdit={handleEdit} onDelete={handleDelete} />
        ) : activeTab === 'whitelabel' ? (
          <WhitelabeledTable
            doctors={whitelabeledDocs}
            onUpdateLimit={updateDoctorLimit}
            onViewReport={viewDoctorReport}
            onDownloadReport={downloadDoctorReport}
            onResetPassword={openPasswordReset}
          />
        ) : (
          <TopDoctorsTable clinics={data?.topClinics || []} />
        )}
      </div>
    </div>
  );
};

const DoctorsManageTable = ({ doctors, onEdit, onDelete }) => {
  if (!doctors || doctors.length === 0) {
    return (
      <div className="p-12 text-center">
        <StethoscopeIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
        <p className="text-[#666]">No doctors found</p>
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
        <tr>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Doctor</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Contact</th>
          <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Priority</th>
          <th className="text-right px-6 py-3 text-xs font-medium text-[#666] uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#f0f0f0]">
        {doctors.map((doctor) => (
          <tr key={doctor.id} className="hover:bg-[#fafafa] transition-colors">
            <td className="px-6 py-4">
              <p className="font-medium text-[#1a1a1a]">{doctor.name}</p>
              <p className="text-xs text-[#999] mt-0.5 truncate max-w-xs">{doctor.address || '—'}</p>
            </td>
            <td className="px-6 py-4 text-sm text-[#666]">
              <p>{doctor.phone || '—'}</p>
              {doctor.whatsapp && (
                <a href={doctor.whatsapp} target="_blank" rel="noopener noreferrer" className="text-green-600 text-xs hover:underline">
                  WhatsApp
                </a>
              )}
            </td>
            <td className="px-6 py-4">
              <span className="inline-flex px-2 py-1 bg-[#f5f5f5] text-[#666] text-xs font-medium rounded-md">
                {doctor.partner_priority || 50}
              </span>
            </td>
            <td className="px-6 py-4 text-right space-x-2">
              <button onClick={() => onEdit(doctor)} className="text-sm text-[#666] hover:text-[#1a1a1a] font-medium">Edit</button>
              <button onClick={() => onDelete(doctor)} className="text-sm text-red-500 hover:text-red-600 font-medium">Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const WhitelabeledTable = ({ doctors, onUpdateLimit, onViewReport, onDownloadReport, onResetPassword }) => {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  if (!doctors || doctors.length === 0) {
    return (
      <div className="p-12 text-center">
        <TagIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
        <p className="text-[#666]">No whitelabeled doctors</p>
      </div>
    );
  }

  const handleEditClick = (doctor) => {
    setEditingId(doctor.id);
    setEditValue(doctor.monthly_limit === null ? '' : doctor.monthly_limit.toString());
  };

  const handleSaveLimit = async (doctor) => {
    const newLimit = editValue === '' ? null : parseInt(editValue, 10);
    if (onUpdateLimit) {
      await onUpdateLimit(doctor.id, newLimit);
    }
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  return (
    <div className="divide-y divide-[#f0f0f0]">
      {doctors.map((doctor) => (
        <div key={doctor.id} className="p-6 flex items-center justify-between hover:bg-[#fafafa] transition-colors">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center text-white text-sm font-bold">
              Dr
            </div>
            <div>
              <p className="font-medium text-[#1a1a1a]">{doctor.url_slug}</p>
              <div className="flex items-center space-x-4 text-xs text-[#999] mt-1">
                <span>{doctor.total_direct_patients || 0} total</span>
                <span>{doctor.this_month_patients || 0} this month</span>
                <span>{doctor.current_usage || 0}/{doctor.monthly_limit || '∞'} used</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Action buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onViewReport && onViewReport(doctor)}
                className="p-2 text-[#666] hover:text-[#1a1a1a] hover:bg-[#f0f0f0] rounded-lg transition-all"
                title="View Report"
              >
                <EyeIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDownloadReport && onDownloadReport(doctor)}
                className="p-2 text-[#666] hover:text-[#1a1a1a] hover:bg-[#f0f0f0] rounded-lg transition-all"
                title="Download PDF"
              >
                <DownloadIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => onResetPassword && onResetPassword(doctor)}
                className="px-3 py-2 text-xs font-medium text-[#666] hover:text-[#1a1a1a] hover:bg-[#f0f0f0] rounded-lg transition-all"
                title="Reset doctor password"
              >
                Reset Password
              </button>
            </div>
            {/* Limit editor */}
            <div className="text-right">
              {editingId === doctor.id ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="∞"
                    className="w-20 px-2 py-1 text-right border border-[#e5e5e5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]"
                    autoFocus
                  />
                  <button
                    onClick={() => handleSaveLimit(doctor)}
                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                  >
                    <CheckIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  className="cursor-pointer group"
                  onClick={() => handleEditClick(doctor)}
                >
                  <p className="text-2xl font-semibold text-[#1a1a1a] group-hover:text-purple-600 transition-colors">
                    {doctor.monthly_limit === null ? '∞' : doctor.monthly_limit}
                  </p>
                  <p className="text-xs text-[#999] group-hover:text-purple-400">click to edit</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TopDoctorsTable = ({ clinics }) => {
  const [reportLoading, setReportLoading] = useState({});
  const [reportStatus, setReportStatus] = useState({});

  // Generate report data (without download)
  const generateDoctorReport = async (clinicId, clinicName) => {
    try {
      setReportLoading(prev => ({ ...prev, [clinicId]: true }));
      setReportStatus(prev => ({ ...prev, [clinicId]: null }));

      const user = localStorage.getItem('admin_user');
      const pass = localStorage.getItem('admin_pass');
      if (!user || !pass) throw new Error('No admin credentials found');

      const currentDate = new Date();
      const reportMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

      const response = await fetch('/api/admin/simple-monthly-reports', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${user}:${pass}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'generate',
          doctorId: clinicId,
          reportMonth: reportMonth
        })
      });

      if (!response.ok) throw new Error(`Report generation failed: ${response.status}`);

      const result = await response.json();
      const referralCount = result.report?.referralCount || result.referrals_count || 0;

      setReportStatus(prev => ({
        ...prev,
        [clinicId]: `✅ Report generated! ${referralCount} referrals for ${reportMonth}`
      }));

      setTimeout(() => {
        setReportStatus(prev => ({ ...prev, [clinicId]: null }));
      }, 5000);

    } catch (error) {
      setReportStatus(prev => ({
        ...prev,
        [clinicId]: `❌ Failed: ${error.message}`
      }));
      setTimeout(() => {
        setReportStatus(prev => ({ ...prev, [clinicId]: null }));
      }, 5000);
    } finally {
      setReportLoading(prev => ({ ...prev, [clinicId]: false }));
    }
  };

  // Handle view report
  const handleViewReport = async (clinic) => {
    setReportLoading(prev => ({ ...prev, [`${clinic.id}_view`]: true }));
    try {
      await viewDoctorReport(clinic);
    } finally {
      setReportLoading(prev => ({ ...prev, [`${clinic.id}_view`]: false }));
    }
  };

  // Handle download report
  const handleDownloadReport = async (clinic) => {
    setReportLoading(prev => ({ ...prev, [`${clinic.id}_download`]: true }));
    setReportStatus(prev => ({ ...prev, [clinic.id]: '📄 Generating PDF...' }));
    try {
      await downloadDoctorReport(clinic);
      setReportStatus(prev => ({ ...prev, [clinic.id]: '✅ PDF downloaded!' }));
      setTimeout(() => {
        setReportStatus(prev => ({ ...prev, [clinic.id]: null }));
      }, 5000);
    } catch (error) {
      setReportStatus(prev => ({ ...prev, [clinic.id]: `❌ Failed: ${error.message}` }));
      setTimeout(() => {
        setReportStatus(prev => ({ ...prev, [clinic.id]: null }));
      }, 5000);
    } finally {
      setReportLoading(prev => ({ ...prev, [`${clinic.id}_download`]: false }));
    }
  };

  if (!clinics || clinics.length === 0) {
    return (
      <div className="p-12 text-center">
        <TrophyIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
        <p className="text-[#666]">No performance data yet</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#f0f0f0]">
      {clinics.map((clinic, idx) => (
        <div key={clinic.id} className="p-6 hover:bg-[#fafafa] transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-[#1a1a1a] rounded-xl flex items-center justify-center text-white text-sm font-bold">
                #{idx + 1}
              </div>
              <div>
                <p className="font-medium text-[#1a1a1a]">{clinic.name || 'Unknown'}</p>
                <div className="flex items-center space-x-4 text-xs text-[#999] mt-1">
                  <span>{clinic.whatsapp_bookings || 0} bookings</span>
                  <span>{clinic.direction_requests || 0} directions</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right mr-4">
                <p className="text-2xl font-semibold text-green-600">{clinic.conversion_rate || 0}%</p>
                <p className="text-xs text-[#999]">conversion</p>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleViewReport(clinic)}
                  disabled={reportLoading[`${clinic.id}_view`]}
                  className="p-2 text-[#666] hover:text-[#1a1a1a] hover:bg-[#f0f0f0] rounded-lg transition-all disabled:opacity-50"
                  title="View Report"
                >
                  {reportLoading[`${clinic.id}_view`] ? (
                    <LoadingSpinner className="w-4 h-4" />
                  ) : (
                    <EyeIcon className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => handleDownloadReport(clinic)}
                  disabled={reportLoading[`${clinic.id}_download`]}
                  className="flex items-center space-x-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                  title="Download PDF Report"
                >
                  {reportLoading[`${clinic.id}_download`] ? (
                    <>
                      <LoadingSpinner className="w-4 h-4" />
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <DownloadIcon className="w-4 h-4" />
                      <span>Download</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          {/* Status Message */}
          {reportStatus[clinic.id] && (
            <div className={`mt-3 text-sm font-medium ${
              reportStatus[clinic.id].startsWith('✅') ? 'text-green-600' :
              reportStatus[clinic.id].startsWith('❌') ? 'text-red-600' : 'text-blue-600'
            }`}>
              {reportStatus[clinic.id]}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Loading spinner component
const LoadingSpinner = ({ className }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ============================================================================
// ACTIVITY SECTION
// ============================================================================

const ActivitySection = ({ data }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [serverLogs, setServerLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Load server logs
  useEffect(() => {
    const loadServerLogs = async () => {
      if (activeTab !== 'logs') return;

      const user = localStorage.getItem('admin_user');
      const pass = localStorage.getItem('admin_pass');
      if (!user || !pass) return;

      setLogsLoading(true);
      try {
        const res = await fetch('/api/admin/logs', {
          headers: { 'Authorization': `Basic ${btoa(`${user}:${pass}`)}` }
        });
        if (res.ok) {
          const result = await res.json();
          setServerLogs(result.items || []);
        }
      } catch (err) {
        // Silent fail
      }
      setLogsLoading(false);
    };

    loadServerLogs();
  }, [activeTab]);

  const getEventStyle = (kind) => {
    const styles = {
      report_generated: { icon: '🧠', bg: 'bg-blue-50', text: 'text-blue-600' },
      doctor_matched: { icon: '🤝', bg: 'bg-green-50', text: 'text-green-600' },
      whatsapp_clicked: { icon: '💬', bg: 'bg-purple-50', text: 'text-purple-600' },
      directions_clicked: { icon: '📍', bg: 'bg-orange-50', text: 'text-orange-600' }
    };
    return styles[kind] || { icon: '⚡', bg: 'bg-gray-50', text: 'text-gray-600' };
  };

  const getLogLevelStyle = (level) => {
    const styles = {
      error: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
      warn: { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200' },
      info: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
      debug: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' }
    };
    return styles[level] || styles.info;
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center space-x-1 bg-white border border-[#e5e5e5] rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('events')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'events'
              ? 'bg-[#1a1a1a] text-white'
              : 'text-[#666] hover:text-[#1a1a1a]'
          }`}
        >
          Recent Events
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'logs'
              ? 'bg-[#1a1a1a] text-white'
              : 'text-[#666] hover:text-[#1a1a1a]'
          }`}
        >
          Server Logs
        </button>
      </div>

      {activeTab === 'events' ? (
        <div className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden">
          <div className="p-6 border-b border-[#e5e5e5]">
            <h3 className="font-semibold text-[#1a1a1a]">Recent Activity</h3>
            <p className="text-sm text-[#666]">Real-time event feed</p>
          </div>

          {data?.recent && data.recent.length > 0 ? (
            <div className="divide-y divide-[#f0f0f0] max-h-[600px] overflow-y-auto">
              {data.recent.map((event, idx) => {
                const style = getEventStyle(event.kind);
                return (
                  <div key={idx} className="p-4 flex items-center space-x-4 hover:bg-[#fafafa] transition-colors">
                    <div className={`w-10 h-10 ${style.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <span className="text-lg">{style.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${style.text}`}>
                        {event.kind.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      {event.clinic_name && (
                        <p className="text-sm text-[#999] truncate">{event.clinic_name}</p>
                      )}
                    </div>
                    <span className="text-xs text-[#999] flex-shrink-0">
                      {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <ActivityIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
              <p className="text-[#666]">No recent activity</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden">
          <div className="p-6 border-b border-[#e5e5e5]">
            <h3 className="font-semibold text-[#1a1a1a]">Server Logs</h3>
            <p className="text-sm text-[#666]">System events and errors</p>
          </div>

          {logsLoading ? (
            <div className="p-12 text-center">
              <div className="w-6 h-6 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-[#666]">Loading logs...</p>
            </div>
          ) : serverLogs.length > 0 ? (
            <div className="divide-y divide-[#f0f0f0] max-h-[600px] overflow-y-auto">
              {serverLogs.map((log, idx) => {
                const style = getLogLevelStyle(log.level);
                return (
                  <div key={idx} className={`p-4 ${style.bg} border-l-4 ${style.border}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded uppercase ${style.text} bg-white`}>
                          {log.level}
                        </span>
                        <p className="text-sm text-[#1a1a1a] mt-2 font-mono break-all">{log.message}</p>
                      </div>
                      <span className="text-xs text-[#999] ml-4 flex-shrink-0">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <TerminalIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
              <p className="text-[#666]">No server logs available</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// API KEYS SECTION
// ============================================================================

const APIKeysTab = ({ credentials }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [revealedKeyResult, setRevealedKeyResult] = useState(null);
  const [revealingKeyId, setRevealingKeyId] = useState(null);
  const [testingKey, setTestingKey] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Create form state
  const [createKeyName, setCreateKeyName] = useState('');
  const [createDoctorId, setCreateDoctorId] = useState('');
  const [createRatePerMinute, setCreateRatePerMinute] = useState(10);
  const [createRatePerDay, setCreateRatePerDay] = useState(1000);
  const [createMonthlyLimit, setCreateMonthlyLimit] = useState('');

  // Edit form state
  const [editKeyName, setEditKeyName] = useState('');
  const [editRatePerMinute, setEditRatePerMinute] = useState(10);
  const [editRatePerDay, setEditRatePerDay] = useState(1000);
  const [editMonthlyLimit, setEditMonthlyLimit] = useState('');

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/api-keys', {
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setKeys(data.keys || []);
      }
    } catch (error) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreateKey = async () => {
    try {
      const response = await fetch('/api/admin/api-keys', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keyName: createKeyName,
          doctorId: createDoctorId || null,
          rateLimitPerMinute: parseInt(createRatePerMinute) || 10,
          rateLimitPerDay: parseInt(createRatePerDay) || 1000,
          monthlyLimit: createMonthlyLimit ? parseInt(createMonthlyLimit) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setNewKeyResult(data);
        setShowCreateModal(false);
        // Reset create form
        setCreateKeyName('');
        setCreateDoctorId('');
        setCreateRatePerMinute(10);
        setCreateRatePerDay(1000);
        setCreateMonthlyLimit('');
        fetchKeys();
      }
    } catch (error) {
    }
  };

  const openEditModal = (key) => {
    setEditKeyName(key.key_name);
    setEditRatePerMinute(key.rate_limit_per_minute);
    setEditRatePerDay(key.rate_limit_per_day);
    setEditMonthlyLimit(key.monthly_limit || '');
    setShowEditModal(key);
  };

  const handleSaveEdit = async () => {
    if (!showEditModal) return;
    await handleUpdateKey(showEditModal.id, {
      keyName: editKeyName,
      rateLimitPerMinute: parseInt(editRatePerMinute),
      rateLimitPerDay: parseInt(editRatePerDay),
      monthlyLimit: editMonthlyLimit ? parseInt(editMonthlyLimit) : null
    });
  };

  // Sandbox test function
  const testApiKey = async (apiKey) => {
    setTestingKey(true);
    setTestResult(null);

    try {
      // Create a small test image (1x1 pixel transparent PNG as base64)
      const testImageData = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

      const response = await fetch('/api/v2/skin-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'MySkinAI-API-Key': apiKey
        },
        body: JSON.stringify({
          imageData: testImageData,
          age: 25,
          primaryConcern: 'acne',
          includeAnnotatedImage: false
        })
      });

      const data = await response.json();

      if (response.ok) {
        setTestResult({ success: true, message: 'API key is working correctly!', details: data });
      } else if (response.status === 401) {
        setTestResult({ success: false, message: 'Authentication failed - key may be invalid', details: data });
      } else if (response.status === 429) {
        setTestResult({ success: true, message: 'API key is valid (rate limited - working as expected)', details: data });
      } else {
        setTestResult({ success: false, message: `API returned error: ${data.error || 'Unknown error'}`, details: data });
      }
    } catch (error) {
      setTestResult({ success: false, message: `Connection error: ${error.message}` });
    }

    setTestingKey(false);
  };

  const handleUpdateKey = async (keyId, updates) => {
    try {
      const response = await fetch(`/api/admin/api-keys?keyId=${keyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setShowEditModal(null);
        fetchKeys();
      }
    } catch (error) {
    }
  };

  const handleDeactivateKey = async (keyId) => {
    if (!confirm('Are you sure you want to deactivate this API key?')) return;

    try {
      await fetch(`/api/admin/api-keys?keyId=${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
        }
      });
      fetchKeys();
    } catch (error) {
    }
  };

  const handleRevealExistingKey = async (keyId) => {
    setRevealingKeyId(keyId);
    try {
      const response = await fetch(`/api/admin/api-keys?keyId=${keyId}&action=reveal`, {
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to reveal key');
        return;
      }

      setRevealedKeyResult(data);
    } catch (error) {
      alert('Failed to reveal key');
    } finally {
      setRevealingKeyId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      {/* New Key Result Modal - INLINE to prevent remounting */}
      {newKeyResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">API Key Created!</h2>
              <p className="text-gray-600 mt-2">Save this key now. It won't be shown again.</p>
            </div>

            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="font-mono text-sm break-all select-all">
                {newKeyResult?.apiKey}
              </div>
            </div>

            <div className="flex space-x-3 mb-4">
              <button
                onClick={() => copyToClipboard(newKeyResult?.apiKey)}
                className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800"
              >
                Copy Key
              </button>
              <button
                onClick={() => testApiKey(newKeyResult?.apiKey)}
                disabled={testingKey}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {testingKey ? 'Testing...' : 'Test Key'}
              </button>
            </div>

            {/* Test Result Display */}
            {testResult && (
              <div className={`p-4 rounded-lg mb-4 ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.message}
                  </span>
                </div>
                {testResult.details && (
                  <pre className="text-xs bg-white/50 p-2 rounded overflow-x-auto max-h-32">
                    {JSON.stringify(testResult.details, null, 2)}
                  </pre>
                )}
              </div>
            )}

            <button
              onClick={() => {
                setNewKeyResult(null);
                setTestResult(null);
              }}
              className="w-full border border-gray-300 py-3 rounded-lg font-medium hover:bg-gray-50"
            >
              Done
            </button>

            <p className="text-red-600 text-sm text-center mt-4">
              Warning: This key will not be shown again!
            </p>
          </div>
        </div>
      )}

      {/* Existing Key Reveal Modal */}
      {revealedKeyResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">API Key Revealed</h2>
              <p className="text-gray-600 mt-2">
                Admin re-view is enabled for this key.
              </p>
            </div>

            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="font-mono text-sm break-all select-all">
                {revealedKeyResult?.apiKey}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => copyToClipboard(revealedKeyResult?.apiKey)}
                className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800"
              >
                Copy Key
              </button>
              <button
                onClick={() => setRevealedKeyResult(null)}
                className="flex-1 border border-gray-300 py-3 rounded-lg font-medium hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Key Modal - INLINE to prevent input focus loss */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Create New API Key</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name *</label>
                <input
                  type="text"
                  value={createKeyName}
                  onChange={(e) => setCreateKeyName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Dr. Smith's Clinic"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Doctor (optional)</label>
                <input
                  type="text"
                  value={createDoctorId}
                  onChange={(e) => setCreateDoctorId(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Doctor ID (UUID)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Requests Per Minute</label>
                  <input
                    type="number"
                    value={createRatePerMinute}
                    onChange={(e) => setCreateRatePerMinute(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Short-burst protection for this key.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Requests Per Day</label>
                  <input
                    type="number"
                    value={createRatePerDay}
                    onChange={(e) => setCreateRatePerDay(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Daily cap before throttling starts.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Request Cap (empty = unlimited)</label>
                <input
                  type="number"
                  value={createMonthlyLimit}
                  onChange={(e) => setCreateMonthlyLimit(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 1000"
                />
                <p className="text-xs text-gray-500 mt-1">Use this for billing-period allocation.</p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleCreateKey}
                disabled={!createKeyName}
                className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                Create Key
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border border-gray-300 py-3 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Key Modal - INLINE to prevent input focus loss */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Edit API Key</h2>
            <p className="text-gray-500 text-sm mb-4">Key: {showEditModal.key_prefix}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                <input
                  type="text"
                  value={editKeyName}
                  onChange={(e) => setEditKeyName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Requests Per Minute</label>
                  <input
                    type="number"
                    value={editRatePerMinute}
                    onChange={(e) => setEditRatePerMinute(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Burst traffic allowance.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Requests Per Day</label>
                  <input
                    type="number"
                    value={editRatePerDay}
                    onChange={(e) => setEditRatePerDay(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">24-hour rolling usage guardrail.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Request Cap</label>
                <input
                  type="number"
                  value={editMonthlyLimit}
                  onChange={(e) => setEditMonthlyLimit(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Empty = unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited monthly volume.</p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSaveEdit}
                className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800"
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(null)}
                className="flex-1 border border-gray-300 py-3 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">API Keys</h2>
          <p className="text-gray-600 text-sm">Manage MySkinAI API access</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800"
        >
          + Create New Key
        </button>
      </div>

      {/* Keys Table */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl border border-[#e5e5e5]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-3 border-b">Key</th>
                <th className="text-left p-3 border-b">Name</th>
                <th className="text-left p-3 border-b">Limits</th>
                <th className="text-left p-3 border-b">Usage</th>
                <th className="text-left p-3 border-b">Status</th>
                <th className="text-left p-3 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} className="hover:bg-gray-50">
                  <td className="p-3 border-b font-mono text-sm">{key.key_prefix}</td>
                  <td className="p-3 border-b font-medium">{key.key_name}</td>
                  <td className="p-3 border-b text-sm">
                    <div>{key.rate_limit_per_minute}/min</div>
                    <div>{key.rate_limit_per_day}/day</div>
                    <div>{key.monthly_limit ? `${key.monthly_limit}/mo` : 'Unlimited'}</div>
                  </td>
                  <td className="p-3 border-b text-sm">
                    <div>This month: {key.current_month_usage}</div>
                    <div>Total: {key.total_usage}</div>
                  </td>
                  <td className="p-3 border-b">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      key.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {key.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-3 border-b">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(key)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRevealExistingKey(key.id)}
                        className="text-emerald-700 hover:text-emerald-900 text-sm"
                        disabled={revealingKeyId === key.id}
                      >
                        {revealingKeyId === key.id ? 'Viewing...' : 'View Key'}
                      </button>
                      {key.is_active && (
                        <button
                          onClick={() => handleDeactivateKey(key.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {keys.length === 0 && (
            <div className="text-center py-8 text-gray-500">No API keys created yet</div>
          )}
        </div>
      )}

      {/* API Documentation */}
      <div className="bg-gray-50 rounded-lg p-6 mt-8">
        <h3 className="font-bold text-gray-900 mb-4">API Usage</h3>
        <div className="space-y-4 text-sm">
          <div>
            <strong>Endpoint:</strong>
            <code className="ml-2 bg-gray-200 px-2 py-1 rounded">POST /api/v2/skin-analysis</code>
          </div>
          <div>
            <strong>Authentication:</strong>
            <code className="ml-2 bg-gray-200 px-2 py-1 rounded">MySkinAI-API-Key: msk_your_key_here</code>
          </div>
          <div>
            <strong>Request Body:</strong>
            <pre className="bg-gray-200 p-3 rounded mt-2 overflow-x-auto">
{`{
  "imageData": "base64_encoded_image",
  "age": 25,
  "primaryConcern": "acne",  // optional
  "includeAnnotatedImage": true  // optional, default true
}`}
            </pre>
          </div>
          <div>
            <strong>Success Response Shape:</strong>
            <pre className="bg-gray-200 p-3 rounded mt-2 overflow-x-auto">
{`{
  "success": true,
  "data": { ...analysis },
  "annotatedImage": "data:image/png;base64,...", // null if includeAnnotatedImage=false
  "meta": {
    "apiVersion": "v2",
    "requestId": "...",
    "timestamp": "...",
    "processingTimeMs": 1234
  }
}`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

const MetricCard = ({ label, value, change, trend, icon, accent = 'default' }) => {
  const accentColors = {
    default: 'bg-[#1a1a1a] text-white',
    green: 'bg-green-500 text-white',
    purple: 'bg-purple-500 text-white',
    orange: 'bg-orange-500 text-white'
  };

  const trendStyles = {
    up: 'bg-green-50 text-green-600',
    down: 'bg-red-50 text-red-600',
    stable: 'bg-gray-50 text-gray-600'
  };

  return (
    <div className="bg-white rounded-2xl border border-[#e5e5e5] p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accentColors[accent]}`}>
          {icon}
        </div>
        {change && (
          <span className={`text-xs font-medium px-2 py-1 rounded-md ${trendStyles[trend] || trendStyles.stable}`}>
            {change}
          </span>
        )}
      </div>
      <p className="text-2xl font-semibold text-[#1a1a1a] mb-1">{value}</p>
      <p className="text-sm text-[#666]">{label}</p>
    </div>
  );
};

const ConversionMetric = ({ label, description, value, color }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <div>
        <p className="font-medium text-[#1a1a1a]">{label}</p>
        <p className="text-xs text-[#999]">{description}</p>
      </div>
      <span className="text-lg font-semibold" style={{ color }}>{value}%</span>
    </div>
    <div className="h-2 bg-[#f0f0f0] rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color }}
      />
    </div>
  </div>
);

const SourceCard = ({ title, subtitle, value, stats, accent }) => {
  const accentColors = {
    purple: 'from-purple-500 to-purple-600',
    blue: 'from-blue-500 to-blue-600'
  };

  return (
    <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className={`w-10 h-10 bg-gradient-to-br ${accentColors[accent]} rounded-xl flex items-center justify-center`}>
          <DocumentIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-[#1a1a1a]">{title}</h3>
          <p className="text-xs text-[#999]">{subtitle}</p>
        </div>
      </div>
      <p className="text-3xl font-semibold text-[#1a1a1a] mb-4">{value.toLocaleString()}</p>
      <div className="grid grid-cols-3 gap-2">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-[#fafafa] rounded-lg p-2 text-center">
            <p className="font-semibold text-[#1a1a1a]">{stat.value.toLocaleString()}</p>
            <p className="text-xs text-[#999]">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FormField = ({ label, name, type = 'text', value, onChange, placeholder, required, step }) => (
  <div>
    <label className="block text-xs font-medium text-[#666] uppercase tracking-wide mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      step={step}
      className="w-full px-3 py-2.5 bg-[#fafafa] border border-[#e5e5e5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent"
    />
  </div>
);

const LoadingState = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-6 h-6 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin" />
  </div>
);

// ============================================================================
// ICONS
// ============================================================================

const BarChartIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const StethoscopeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

const ActivityIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
  </svg>
);

const ChevronIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const LogoutIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
  </svg>
);

const RefreshIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const SearchIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const DocumentIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const MessageIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
  </svg>
);

const MapIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);

const TagIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
  </svg>
);

const TrophyIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
  </svg>
);

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const DownloadIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const TerminalIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const KeyIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
  </svg>
);

const InboxIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z" />
  </svg>
);

const InvoiceIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

// ============================================================================
// KEY REQUESTS TAB - Admin approval workflow for developer API key requests
// ============================================================================

const KeyRequestsTab = ({ credentials, onCountChange }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    rateLimitPerMinute: 10,
    rateLimitPerDay: 1000,
    monthlyLimit: ''
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await fetch(`/api/admin/api-key-requests${params}`, {
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
        if (onCountChange) {
          onCountChange(data.pendingCount || 0);
        }
      } else {
        setError('Failed to load requests');
      }
    } catch (err) {
      setError('Failed to load requests');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const response = await fetch('/api/admin/api-key-requests', {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requestId: selectedRequest.id,
          action: 'approve',
          rateLimitPerMinute: parseInt(approvalForm.rateLimitPerMinute) || 10,
          rateLimitPerDay: parseInt(approvalForm.rateLimitPerDay) || 1000,
          monthlyLimit: approvalForm.monthlyLimit ? parseInt(approvalForm.monthlyLimit) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`API key created: ${data.apiKey}\n\nSave this key - it won't be shown again!`);
        setSelectedRequest(null);
        fetchRequests();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to approve request');
      }
    } catch (err) {
      alert('Failed to approve request');
    }
    setProcessing(false);
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      const response = await fetch('/api/admin/api-key-requests', {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requestId: selectedRequest.id,
          action: 'reject',
          rejectionReason: rejectionReason.trim()
        })
      });

      if (response.ok) {
        setSelectedRequest(null);
        setRejectionReason('');
        fetchRequests();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to reject request');
      }
    } catch (err) {
      alert('Failed to reject request');
    }
    setProcessing(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#1a1a1a]">API Key Requests</h2>
          <p className="text-sm text-[#666]">Review and approve developer API key requests</p>
        </div>
        <div className="flex items-center space-x-1 bg-white border border-[#e5e5e5] rounded-xl p-1">
          {['pending', 'approved', 'rejected', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                filter === status
                  ? 'bg-[#1a1a1a] text-white'
                  : 'text-[#666] hover:text-[#1a1a1a]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Requests List */}
      <div className="bg-white rounded-2xl border border-[#e5e5e5] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-6 h-6 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-[#666]">Loading...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center">
            <InboxIcon className="w-8 h-8 text-[#ccc] mx-auto mb-3" />
            <p className="text-[#666]">No {filter !== 'all' ? filter : ''} requests</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase">Date</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase">Developer</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase">Purpose</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-[#666] uppercase">Status</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-[#666] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f0f0f0]">
              {requests.map(req => (
                <tr key={req.id} className="hover:bg-[#fafafa] transition-colors">
                  <td className="px-6 py-4 text-sm text-[#666]">{formatDate(req.created_at)}</td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-[#1a1a1a]">{req.name}</p>
                    <p className="text-xs text-[#999]">{req.email}</p>
                    {req.organization && <p className="text-xs text-[#999]">{req.organization}</p>}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#666] max-w-xs truncate">{req.purpose}</td>
                  <td className="px-6 py-4">{getStatusBadge(req.status)}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setSelectedRequest(req)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {req.status === 'pending' ? 'Review' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#e5e5e5]">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1a1a1a]">Review Request</h3>
                  <p className="text-sm text-[#666]">{formatDate(selectedRequest.created_at)}</p>
                </div>
                {getStatusBadge(selectedRequest.status)}
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Developer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#999] uppercase mb-1">Name</p>
                  <p className="font-medium text-[#1a1a1a]">{selectedRequest.name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#999] uppercase mb-1">Email</p>
                  <p className="text-[#1a1a1a]">{selectedRequest.email}</p>
                </div>
                {selectedRequest.organization && (
                  <div className="col-span-2">
                    <p className="text-xs text-[#999] uppercase mb-1">Organization</p>
                    <p className="text-[#1a1a1a]">{selectedRequest.organization}</p>
                  </div>
                )}
              </div>

              {/* Purpose */}
              <div>
                <p className="text-xs text-[#999] uppercase mb-1">Purpose</p>
                <p className="text-[#1a1a1a] bg-[#fafafa] p-3 rounded-lg">{selectedRequest.purpose}</p>
              </div>

              {selectedRequest.expected_usage && (
                <div>
                  <p className="text-xs text-[#999] uppercase mb-1">Expected Usage</p>
                  <p className="text-[#1a1a1a]">{selectedRequest.expected_usage}</p>
                </div>
              )}

              {selectedRequest.use_case && (
                <div>
                  <p className="text-xs text-[#999] uppercase mb-1">Use Case</p>
                  <p className="text-[#1a1a1a]">{selectedRequest.use_case}</p>
                </div>
              )}

              {/* Rejection reason if rejected */}
              {selectedRequest.status === 'rejected' && selectedRequest.rejection_reason && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-xs text-red-600 uppercase mb-1">Rejection Reason</p>
                  <p className="text-red-800">{selectedRequest.rejection_reason}</p>
                </div>
              )}

              {/* Approval Form (pending only) */}
              {selectedRequest.status === 'pending' && (
                <div className="border-t border-[#e5e5e5] pt-6 space-y-4">
                  <h4 className="font-medium text-[#1a1a1a]">Set Rate Limits</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-[#666] mb-1">Requests/Minute</label>
                      <input
                        type="number"
                        value={approvalForm.rateLimitPerMinute}
                        onChange={e => setApprovalForm({...approvalForm, rateLimitPerMinute: e.target.value})}
                        className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg text-sm"
                      />
                      <p className="text-[11px] text-[#888] mt-1">Burst limit</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#666] mb-1">Requests/Day</label>
                      <input
                        type="number"
                        value={approvalForm.rateLimitPerDay}
                        onChange={e => setApprovalForm({...approvalForm, rateLimitPerDay: e.target.value})}
                        className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg text-sm"
                      />
                      <p className="text-[11px] text-[#888] mt-1">Daily cap</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#666] mb-1">Monthly Cap</label>
                      <input
                        type="number"
                        value={approvalForm.monthlyLimit}
                        onChange={e => setApprovalForm({...approvalForm, monthlyLimit: e.target.value})}
                        className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg text-sm"
                        placeholder="Unlimited"
                      />
                      <p className="text-[11px] text-[#888] mt-1">Billing-cycle limit</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-[#666] mb-1">Rejection Reason (if rejecting)</label>
                    <textarea
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e.target.value)}
                      className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg text-sm resize-none"
                      rows={2}
                      placeholder="Optional - required if rejecting"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-[#e5e5e5] flex justify-end space-x-3">
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-[#666] hover:text-[#1a1a1a] text-sm font-medium"
              >
                Close
              </button>
              {selectedRequest.status === 'pending' && (
                <>
                  <button
                    onClick={handleReject}
                    disabled={processing}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve & Generate Key
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// INVOICES SECTION - Generate branded invoices for doctor billing
// ============================================================================

const DOCTOR_INFO_MAP = {
  rohanbhattacharjee: { name: 'Dr. Rohan Bhattacharjee', clinic: "Dr. Rohan's Skin, LASERs, & Hair transplantation clinic" },
};

const INVOICE_PLANS = [
  { id: '1month', name: '1 Month', reports: 500, perReport: 10, total: 5000, months: 1 },
  { id: '3months', name: '3 Months', reports: 1500, perReport: 9, total: 13500, months: 3 },
  { id: '6months', name: '6 Months', reports: 3000, perReport: 8, total: 24000, months: 6 },
  { id: 'yearly', name: 'Yearly', reports: 6000, perReport: 7, total: 42000, months: 12 },
];

const InvoicesSection = ({ credentials, data }) => {
  const [form, setForm] = useState({
    doctorId: '',
    planId: '1month',
    invoiceDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [generating, setGenerating] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);

  // Load whitelabeled doctors
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const user = localStorage.getItem('admin_user');
        const pass = localStorage.getItem('admin_pass');
        if (!user || !pass) {
          setLoadingDoctors(false);
          return;
        }

        const response = await fetch('/api/admin/whitelabeled-docs', {
          headers: { 'Authorization': `Basic ${btoa(`${user}:${pass}`)}` }
        });
        if (response.ok) {
          const result = await response.json();
          setDoctors(result.doctors || []);
        }
      } catch (err) {
      } finally {
        setLoadingDoctors(false);
      }
    };
    fetchDoctors();
  }, []);

  const selectedPlan = INVOICE_PLANS.find(p => p.id === form.planId);
  const selectedDoctor = doctors.find(d => d.id === form.doctorId);

  const generateInvoiceNumber = () => {
    const date = new Date(form.invoiceDate);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const seq = String(Math.floor(Math.random() * 9000) + 1000);
    return `RE-${yyyy}${mm}-${seq}`;
  };

  const getBillingPeriod = () => {
    const start = new Date(form.invoiceDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + (selectedPlan?.months || 1));
    end.setDate(end.getDate() - 1);
    const fmt = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${fmt(start)} - ${fmt(end)}`;
  };

  const handleGenerate = async () => {
    if (!form.doctorId || !selectedPlan) return;
    setGenerating(true);

    try {
      const { default: jsPDF } = await import('jspdf');

      const invoiceNumber = generateInvoiceNumber();
      const billingPeriod = getBillingPeriod();
      const invoiceDate = new Date(form.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
      const dueDate = (() => {
        const d = new Date(form.invoiceDate);
        d.setDate(d.getDate() + 15);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
      })();
      const docInfo = DOCTOR_INFO_MAP[selectedDoctor?.url_slug] || {};
      const doctorName = docInfo.name || selectedDoctor?.url_slug || 'Doctor';
      const clinicName = docInfo.clinic || '';
      const fmt = (n) => 'Rs. ' + Number(n).toLocaleString('en-IN');
      const total = selectedPlan.total;

      // ── Number to words (Indian numbering system) ──
      const numberToWords = (n) => {
        if (n === 0) return 'Zero';
        const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
        const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
        const convert = (num) => {
          if (num < 20) return ones[num];
          if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
          if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + convert(num % 100) : '');
          if (num < 100000) return convert(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + convert(num % 1000) : '');
          if (num < 10000000) return convert(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + convert(num % 100000) : '');
          return convert(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + convert(num % 10000000) : '');
        };
        return convert(Math.floor(n));
      };

      // ── Load logo (SVG → canvas → base64 PNG) ──
      let logoBase64 = null;
      try {
        const resp = await fetch('/assets/LOGO.svg');
        let svgText = await resp.text();
        svgText = svgText.replace(/viewBox="0 0 21000 29700"/, 'viewBox="2500 7000 16000 15700"');
        svgText = svgText.replace(/width="210mm"/, 'width="400"');
        svgText = svgText.replace(/height="297mm"/, 'height="392"');
        const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 392;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 400, 392);
        logoBase64 = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
      } catch (e) {
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const mx = 18;
      const rx = pw - mx;
      let y = 0;

      const roundRect = (x, yy, w, h, r, fill) => {
        pdf.setFillColor(...fill);
        pdf.roundedRect(x, yy, w, h, r, r, 'F');
      };

      // ── HEADER ──
      y = 18;

      if (logoBase64) {
        pdf.addImage(logoBase64, 'PNG', mx, y - 6, 12, 12);
      } else {
        roundRect(mx, y - 6, 12, 12, 2, [236, 51, 188]);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(6);
        pdf.setTextColor(255, 255, 255);
        pdf.text('my-skin.ai', mx + 6, y + 1.5, { align: 'center' });
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(236, 51, 188);
      pdf.text('Rayora Enterprise', mx + 16, y + 2);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(150, 150, 150);
      pdf.text('AI-Powered Healthcare Solutions', mx + 16, y + 7);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(140, 140, 140);
      let addrY = y - 2;
      ['Rayora Enterprise', 'rayoraenterprise@gmail.com'].forEach(line => {
        pdf.text(line, rx, addrY, { align: 'right' });
        addrY += 4;
      });

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(20);
      pdf.setTextColor(236, 51, 188);
      pdf.text('INVOICE', rx, addrY + 4, { align: 'right' });

      // ── BILLED TO (gray background band) ──
      y += 18;
      const billedBgH = clinicName ? 22 : 16;
      roundRect(mx, y, rx - mx, billedBgH, 2.5, [245, 245, 247]);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(140, 140, 140);
      pdf.text('Billed to', mx + 5, y + 5);

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(30, 30, 30);
      pdf.text(doctorName, mx + 5, y + 11);

      if (clinicName) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(90, 90, 90);
        const clLines = pdf.splitTextToSize(clinicName, 130);
        pdf.text(clLines, mx + 5, y + 16);
      }

      // ── INVOICE INFO (light pink band) ──
      y += billedBgH + 6;
      const infoH = 18;
      roundRect(mx, y, rx - mx, infoH, 2.5, [253, 243, 251]);

      const infoY = y + 5;
      const infoCols = [mx + 5, mx + 42, mx + 82, mx + 122];

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(140, 140, 140);
      pdf.text('Due date', infoCols[0], infoY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(30, 30, 30);
      pdf.text(dueDate, infoCols[0], infoY + 5);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(140, 140, 140);
      pdf.text('Invoice date', infoCols[1], infoY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(30, 30, 30);
      pdf.text(invoiceDate, infoCols[1], infoY + 5);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(140, 140, 140);
      pdf.text('Invoice number', infoCols[2], infoY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(30, 30, 30);
      pdf.text(invoiceNumber, infoCols[2], infoY + 5);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(140, 140, 140);
      pdf.text('Billing period', infoCols[3], infoY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(30, 30, 30);
      pdf.text(billingPeriod, infoCols[3], infoY + 5);

      // ── LINE ITEMS TABLE ──
      y += infoH + 10;
      const tableW = rx - mx;

      const thH = 9;
      roundRect(mx, y, tableW, thH, 1.5, [253, 243, 251]);

      const colDesc = mx + 5;
      const colQty = mx + 100;
      const colRate = mx + 128;
      const colAmt = rx - 5;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(120, 120, 120);
      const thY = y + 6;
      pdf.text('Item description', colDesc, thY);
      pdf.text('Qty', colQty, thY, { align: 'center' });
      pdf.text('Rate', colRate, thY, { align: 'center' });
      pdf.text('Amount', colAmt, thY, { align: 'right' });

      y += thH + 1;
      const rowBgH = 14;
      roundRect(mx, y, tableW, rowBgH, 1, [253, 248, 252]);

      const rowY = y + 6;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(30, 30, 30);
      pdf.text(`White-Label Plan \u2014 ${selectedPlan.name}`, colDesc, rowY);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(130, 130, 130);
      pdf.text('AI Skin Analysis Reports', colDesc, rowY + 5);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(30, 30, 30);
      pdf.text(selectedPlan.reports.toLocaleString('en-IN'), colQty, rowY, { align: 'center' });
      pdf.text(fmt(selectedPlan.perReport), colRate, rowY, { align: 'center' });

      pdf.setFont('helvetica', 'bold');
      pdf.text(fmt(total), colAmt, rowY, { align: 'right' });

      y += rowBgH + 2;
      pdf.setDrawColor(235, 235, 235);
      pdf.setLineWidth(0.3);
      pdf.line(mx, y, rx, y);

      // ── TOTALS ──
      y += 8;
      const totLabelX = rx - 68;
      const totValX = rx - 5;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(130, 130, 130);
      pdf.text('Subtotal', totLabelX, y);
      pdf.setTextColor(30, 30, 30);
      pdf.text(fmt(total), totValX, y, { align: 'right' });

      y += 6;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(130, 130, 130);
      pdf.text('Total', totLabelX, y);
      pdf.setTextColor(30, 30, 30);
      pdf.text(fmt(total), totValX, y, { align: 'right' });

      y += 8;

      // Total due bar (prominent pink)
      const tdBarH = 13;
      roundRect(totLabelX - 5, y - 4, rx - totLabelX + 5, tdBarH, 3, [236, 51, 188]);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Total due', totLabelX, y + 4);
      pdf.text(fmt(total), rx - 5, y + 4, { align: 'right' });

      // ── AMOUNT IN WORDS ──
      y += tdBarH + 6;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(130, 130, 130);
      pdf.text(`INR ${numberToWords(total)} Only`, mx, y);

      if (form.notes) {
        y += 10;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Notes:', mx, y);
        pdf.setFont('helvetica', 'normal');
        const nw = pdf.getTextWidth('Notes: ');
        pdf.text(form.notes, mx + nw, y);
      }

      // ── FOOTER ──
      const thankY = ph - 36;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(30, 30, 30);
      pdf.text('Thank you for the business!', mx, thankY);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(130, 130, 130);
      pdf.text('Payment is due within 15 days of the invoice date.', mx, thankY + 6);
      pdf.text('Bank transfer details will be shared separately.', mx, thankY + 11);

      // Bottom contact bar
      const barY = ph - 14;
      pdf.setFillColor(253, 243, 251);
      pdf.rect(0, barY, pw, 14, 'F');
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(140, 140, 140);
      pdf.text('Rayora Enterprise  \u2022  rayoraenterprise@gmail.com', pw / 2, barY + 8, { align: 'center' });

      pdf.save(`Invoice-${invoiceNumber}-${selectedDoctor?.url_slug || 'doctor'}.pdf`);
    } catch (err) {
      alert('Failed to generate invoice. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Invoice Form */}
      <div className="bg-white rounded-xl border border-[#e5e5e5] p-6">
        <h2 className="text-lg font-semibold text-[#1a1a1a] mb-6">Generate Invoice</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Doctor Selector */}
          <div>
            <label className="block text-sm font-medium text-[#666] mb-2">Doctor / Clinic</label>
            {loadingDoctors ? (
              <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
            ) : (
              <select
                value={form.doctorId}
                onChange={(e) => setForm(f => ({ ...f, doctorId: e.target.value }))}
                className="w-full px-3 py-2.5 border border-[#e5e5e5] rounded-lg text-sm text-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/10 focus:border-[#ccc]"
              >
                <option value="">Select a doctor...</option>
                {doctors.map(doc => {
                  const info = DOCTOR_INFO_MAP[doc.url_slug] || {};
                  return (
                    <option key={doc.id} value={doc.id}>
                      {info.name || doc.url_slug} {info.clinic ? `- ${info.clinic}` : ''}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {/* Plan Selector */}
          <div>
            <label className="block text-sm font-medium text-[#666] mb-2">Plan</label>
            <select
              value={form.planId}
              onChange={(e) => setForm(f => ({ ...f, planId: e.target.value }))}
              className="w-full px-3 py-2.5 border border-[#e5e5e5] rounded-lg text-sm text-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/10 focus:border-[#ccc]"
            >
              {INVOICE_PLANS.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} - {plan.reports.toLocaleString('en-IN')} reports @ \u20B9{plan.perReport}/report
                </option>
              ))}
            </select>
          </div>

          {/* Invoice Date */}
          <div>
            <label className="block text-sm font-medium text-[#666] mb-2">Invoice Date</label>
            <input
              type="date"
              value={form.invoiceDate}
              onChange={(e) => setForm(f => ({ ...f, invoiceDate: e.target.value }))}
              className="w-full px-3 py-2.5 border border-[#e5e5e5] rounded-lg text-sm text-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/10 focus:border-[#ccc]"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#666] mb-2">Notes (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Additional notes for the invoice..."
              className="w-full px-3 py-2.5 border border-[#e5e5e5] rounded-lg text-sm text-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/10 focus:border-[#ccc]"
            />
          </div>
        </div>

        {/* Summary */}
        {selectedPlan && (
          <div className="mt-6 p-4 bg-[#fafafa] rounded-lg border border-[#f0f0f0]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs font-medium text-[#888] uppercase tracking-wide">Plan</div>
                <div className="text-sm font-semibold text-[#1a1a1a] mt-1">{selectedPlan.name}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-[#888] uppercase tracking-wide">Reports</div>
                <div className="text-sm font-semibold text-[#1a1a1a] mt-1">{selectedPlan.reports.toLocaleString('en-IN')}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-[#888] uppercase tracking-wide">Per Report</div>
                <div className="text-sm font-semibold text-[#1a1a1a] mt-1">{'\u20B9'}{selectedPlan.perReport}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-[#888] uppercase tracking-wide">Total</div>
                <div className="text-lg font-bold text-[#1a1a1a] mt-1">{'\u20B9'}{selectedPlan.total.toLocaleString('en-IN')}</div>
              </div>
            </div>
            {form.invoiceDate && (
              <div className="mt-3 pt-3 border-t border-[#e5e5e5]">
                <div className="text-xs font-medium text-[#888] uppercase tracking-wide">Billing Period</div>
                <div className="text-sm text-[#1a1a1a] mt-1">{getBillingPeriod()}</div>
              </div>
            )}
          </div>
        )}

        {/* Generate Button */}
        <div className="mt-6">
          <button
            onClick={handleGenerate}
            disabled={!form.doctorId || generating}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-[#1a1a1a] text-white rounded-lg text-sm font-semibold hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {generating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Generate & Download PDF</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Pricing Reference */}
      <div className="bg-white rounded-xl border border-[#e5e5e5] p-6">
        <h3 className="text-sm font-semibold text-[#1a1a1a] mb-4">Plan Reference</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e5e5e5]">
                <th className="text-left py-3 px-2 text-[#888] font-medium text-xs uppercase tracking-wide">Plan</th>
                <th className="text-center py-3 px-2 text-[#888] font-medium text-xs uppercase tracking-wide">Reports</th>
                <th className="text-center py-3 px-2 text-[#888] font-medium text-xs uppercase tracking-wide">Per Report</th>
                <th className="text-right py-3 px-2 text-[#888] font-medium text-xs uppercase tracking-wide">Total</th>
                <th className="text-right py-3 px-2 text-[#888] font-medium text-xs uppercase tracking-wide">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {INVOICE_PLANS.map(plan => (
                <tr key={plan.id} className="border-b border-[#f5f5f5] last:border-0">
                  <td className="py-3 px-2 font-medium text-[#1a1a1a]">{plan.name}</td>
                  <td className="py-3 px-2 text-center text-[#666]">{plan.reports.toLocaleString('en-IN')}</td>
                  <td className="py-3 px-2 text-center text-[#666]">{'\u20B9'}{plan.perReport}</td>
                  <td className="py-3 px-2 text-right font-semibold text-[#1a1a1a]">{'\u20B9'}{plan.total.toLocaleString('en-IN')}</td>
                  <td className="py-3 px-2 text-right text-[#666]">{'\u20B9'}{(plan.total / plan.months).toLocaleString('en-IN')}/mo</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
